// src/pages/GestionUtilisateurs.jsx
import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { usersApi } from '../services/api'

const ROLES = [
  { value: 'super_admin',  label: '👑 Super Admin',   desc: 'Accès total + gestion utilisateurs' },
  { value: 'gestionnaire', label: '🔧 Gestionnaire',  desc: 'Tout sauf gestion utilisateurs' },
  { value: 'commercial',   label: '💼 Commercial',    desc: 'Créer devis/factures, pas de suppression' },
  { value: 'comptable',    label: '📊 Comptable',     desc: 'Lecture seule + validation factures' },
  { value: 'lecture',      label: '👁️ Lecture',       desc: 'Consultation uniquement' },
]

const ROLE_COLORS = {
  super_admin:  { bg: '#7C3AED20', border: '#7C3AED', text: '#C4B5FD' },
  gestionnaire: { bg: '#2563EB20', border: '#2563EB', text: '#93C5FD' },
  commercial:   { bg: '#05966920', border: '#059669', text: '#6EE7B7' },
  comptable:    { bg: '#D9770620', border: '#D97706', text: '#FCD34D' },
  lecture:      { bg: '#64748B20', border: '#64748B', text: '#CBD5E1' },
}

// Miroir de AuthContext — sert à afficher les valeurs par défaut du rôle dans la grille
const PERMISSIONS = {
  super_admin: {
    documents:    { read: true,  write: true,  delete: true  },
    clients:      { read: true,  write: true,  delete: true  },
    fournisseurs: { read: true,  write: true,  delete: true  },
    catalogue:    { read: true,  write: true,  delete: true  },
    bc:           { read: true,  write: true,  delete: true  },
    settings:     { read: true,  write: true                 },
    users:        { read: true,  write: true,  delete: true  },
    import:       { read: true,  write: true                 },
  },
  gestionnaire: {
    documents:    { read: true,  write: true,  delete: true  },
    clients:      { read: true,  write: true,  delete: true  },
    fournisseurs: { read: true,  write: true,  delete: true  },
    catalogue:    { read: true,  write: true,  delete: true  },
    bc:           { read: true,  write: true,  delete: true  },
    settings:     { read: true,  write: true                 },
    users:        { read: false, write: false, delete: false },
    import:       { read: false, write: false                },
  },
  commercial: {
    documents:    { read: true,  write: true,  delete: false },
    clients:      { read: true,  write: true,  delete: false },
    fournisseurs: { read: true,  write: true,  delete: false },
    catalogue:    { read: true,  write: true,  delete: false },
    bc:           { read: true,  write: true,  delete: false },
    settings:     { read: true,  write: false               },
    users:        { read: false, write: false, delete: false },
    import:       { read: false, write: false                },
  },
  comptable: {
    documents:    { read: true,  write: false, delete: false },
    clients:      { read: true,  write: false, delete: false },
    fournisseurs: { read: true,  write: false, delete: false },
    catalogue:    { read: true,  write: false, delete: false },
    bc:           { read: true,  write: false, delete: false },
    settings:     { read: true,  write: false               },
    users:        { read: false, write: false, delete: false },
    import:       { read: false, write: false                },
  },
  lecture: {
    documents:    { read: true,  write: false, delete: false },
    clients:      { read: true,  write: false, delete: false },
    fournisseurs: { read: true,  write: false, delete: false },
    catalogue:    { read: true,  write: false, delete: false },
    bc:           { read: true,  write: false, delete: false },
    settings:     { read: false, write: false               },
    users:        { read: false, write: false, delete: false },
    import:       { read: false, write: false                },
  },
}

const MODULES = [
  { key: 'documents',    label: '📄 Documents',        hasDelete: true  },
  { key: 'clients',      label: '👥 Clients',           hasDelete: true  },
  { key: 'fournisseurs', label: '🏭 Fournisseurs',      hasDelete: true  },
  { key: 'catalogue',    label: '📦 Catalogue',         hasDelete: true  },
  { key: 'bc',           label: '🛒 Bons de commande',  hasDelete: true  },
  { key: 'settings',     label: '⚙️ Paramètres',        hasDelete: false },
  { key: 'users',        label: '👤 Utilisateurs',      hasDelete: false },
  { key: 'import',       label: '📥 Import / Sauvegarde', hasDelete: false },
]

export default function GestionUtilisateurs() {
  const { profil: moi, isAdmin } = useAuth()
  const [users,            setUsers]            = useState([])
  const [loading,          setLoading]          = useState(true)
  const [showInvite,       setShowInvite]       = useState(false)
  const [invite,           setInvite]           = useState({ email: '', nom: '', role: 'commercial' })
  const [invLoading,       setInvLoading]       = useState(false)
  const [toast,            setToast]            = useState(null)
  const [expandedUserId,   setExpandedUserId]   = useState(null)
  const [permissionsEnCours, setPermissionsEnCours] = useState({}) // userId → draft permissions_custom
  const [savingPerm,       setSavingPerm]       = useState(false)

  const notify = (type, text) => {
    setToast({ type, text })
    setTimeout(() => setToast(null), 4000)
  }

  useEffect(() => { charger() }, [])

  const charger = async () => {
    setLoading(true)
    try { setUsers(await usersApi.liste()) }
    catch { notify('error', 'Impossible de charger les utilisateurs.') }
    finally { setLoading(false) }
  }

  const handleInviter = async (e) => {
    e.preventDefault()
    setInvLoading(true)
    try {
      await usersApi.inviter(invite.email, invite.nom, invite.role)
      notify('success', `Invitation envoyée à ${invite.email}`)
      setInvite({ email: '', nom: '', role: 'commercial' })
      setShowInvite(false)
      charger()
    } catch (err) {
      notify('error', err.message || "Erreur lors de l'invitation.")
    } finally { setInvLoading(false) }
  }

  const handleRole = async (id, role) => {
    try {
      await usersApi.changerRole(id, role)
      setUsers(prev => prev.map(u => u.id === id ? { ...u, role } : u))
      notify('success', 'Rôle mis à jour.')
    } catch { notify('error', 'Erreur changement de rôle.') }
  }

  const handleDesactiver = async (id) => {
    if (!window.confirm('Désactiver cet utilisateur ?')) return
    try {
      await usersApi.desactiver(id)
      setUsers(prev => prev.map(u => u.id === id ? { ...u, actif: false } : u))
    } catch { notify('error', 'Erreur.') }
  }

  // Ouvrir / fermer le panneau d'accès d'un utilisateur
  const handleToggleAcces = (userId) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null)
      return
    }
    setExpandedUserId(userId)
    const user = users.find(u => u.id === userId)
    setPermissionsEnCours(prev => ({
      ...prev,
      [userId]: JSON.parse(JSON.stringify(user?.permissions_custom || {}))
    }))
  }

  // Basculer une permission dans le draft
  const handleTogglePermission = (userId, resource, action) => {
    const user = users.find(u => u.id === userId)
    const roleDefault = PERMISSIONS[user.role]?.[resource]?.[action] ?? false
    const draft = permissionsEnCours[userId] || {}
    const currentCustom = draft[resource]?.[action]
    const currentEffective = currentCustom !== undefined ? currentCustom : roleDefault
    const newValue = !currentEffective

    setPermissionsEnCours(prev => {
      const newDraft = JSON.parse(JSON.stringify(prev[userId] || {}))
      if (newValue === roleDefault) {
        // Même valeur que le rôle → supprimer l'exception
        if (newDraft[resource]) {
          delete newDraft[resource][action]
          if (Object.keys(newDraft[resource]).length === 0) delete newDraft[resource]
        }
      } else {
        // Valeur différente du rôle → stocker l'exception
        if (!newDraft[resource]) newDraft[resource] = {}
        newDraft[resource][action] = newValue
      }
      return { ...prev, [userId]: newDraft }
    })
  }

  // Sauvegarder les permissions d'un utilisateur
  const handleSauvegarder = async (userId) => {
    setSavingPerm(true)
    try {
      const draft = permissionsEnCours[userId] || {}
      const updated = await usersApi.mettreAJourPermissions(userId, draft)
      setUsers(prev => prev.map(u => u.id === userId ? updated : u))
      notify('success', 'Accès enregistrés.')
      setExpandedUserId(null)
    } catch {
      notify('error', 'Erreur lors de la sauvegarde.')
    } finally {
      setSavingPerm(false)
    }
  }

  // Réinitialiser toutes les exceptions d'un utilisateur (retour au rôle pur)
  const handleResetPermissions = async (userId) => {
    if (!window.confirm('Réinitialiser tous les accès personnalisés ? L\'utilisateur suivra uniquement son rôle.')) return
    setSavingPerm(true)
    try {
      const updated = await usersApi.mettreAJourPermissions(userId, {})
      setUsers(prev => prev.map(u => u.id === userId ? updated : u))
      setPermissionsEnCours(prev => ({ ...prev, [userId]: {} }))
      notify('success', 'Accès réinitialisés.')
    } catch {
      notify('error', 'Erreur lors de la réinitialisation.')
    } finally {
      setSavingPerm(false)
    }
  }

  // Vérifie si un utilisateur a des accès personnalisés (exceptions au rôle)
  const aDesAccesPersonnalises = (user) =>
    user.permissions_custom && Object.keys(user.permissions_custom).length > 0

  if (!isAdmin()) return (
    <div style={{ padding: '2rem', color: '#EF4444', textAlign: 'center', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
      🚫 Accès réservé aux administrateurs.
    </div>
  )

  const inp = (extra = {}) => ({
    padding: '0.8rem 1rem', borderRadius: '10px',
    border: '1.5px solid rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.05)', color: 'white',
    fontSize: '0.9rem', outline: 'none', width: '100%', boxSizing: 'border-box',
    ...extra
  })

  // Rendu de la grille de permissions pour un utilisateur
  const renderPanneauAcces = (user) => {
    const draft = permissionsEnCours[user.id] || {}
    const hasChanges = JSON.stringify(draft) !== JSON.stringify(user.permissions_custom || {})

    return (
      <tr key={`panel-${user.id}`}>
        <td colSpan={6} style={{ padding: 0, borderTop: 'none' }}>
          <div style={{
            margin: '0 1rem 1rem',
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,107,53,0.2)',
            borderRadius: 14,
            padding: '1.25rem 1.5rem',
          }}>
            {/* En-tête du panneau */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div>
                <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#E2E8F0' }}>
                  🔐 Configuration des accès — {user.nom} {user.prenom || ''}
                </span>
                <div style={{ color: '#64748B', fontSize: '0.75rem', marginTop: '0.2rem' }}>
                  Cases grises = hérité du rôle · Cases orange = exception personnalisée
                </div>
              </div>
              <button
                onClick={() => handleResetPermissions(user.id)}
                disabled={savingPerm || !aDesAccesPersonnalises(user)}
                style={{
                  background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.3)',
                  color: aDesAccesPersonnalises(user) ? '#FCA5A5' : '#4B5563',
                  borderRadius: 8, padding: '0.35rem 0.85rem',
                  cursor: aDesAccesPersonnalises(user) ? 'pointer' : 'not-allowed',
                  fontSize: '0.78rem', fontWeight: 600,
                }}
              >
                🔄 Réinitialiser
              </button>
            </div>

            {/* Grille des permissions */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#64748B', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', width: '40%' }}>
                      Module
                    </th>
                    {['Consulter', 'Créer / Modifier', 'Supprimer'].map(col => (
                      <th key={col} style={{ textAlign: 'center', padding: '0.5rem 0.75rem', color: '#64748B', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {MODULES.map(mod => {
                    const actions = [
                      { key: 'read',   label: 'Consulter'       },
                      { key: 'write',  label: 'Créer / Modifier' },
                      { key: 'delete', label: 'Supprimer'        },
                    ]
                    return (
                      <tr key={mod.key} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                        <td style={{ padding: '0.6rem 0.75rem', fontSize: '0.875rem', fontWeight: 500, color: '#CBD5E1' }}>
                          {mod.label}
                        </td>
                        {actions.map(action => {
                          // Supprimer non applicable pour settings
                          if (action.key === 'delete' && !mod.hasDelete) {
                            return (
                              <td key={action.key} style={{ textAlign: 'center', padding: '0.6rem 0.75rem' }}>
                                <span style={{ color: '#334155', fontSize: '0.8rem' }}>—</span>
                              </td>
                            )
                          }

                          const roleDefault = PERMISSIONS[user.role]?.[mod.key]?.[action.key] ?? false
                          const customVal   = draft[mod.key]?.[action.key]
                          const isCustom    = customVal !== undefined
                          const effective   = isCustom ? customVal : roleDefault

                          return (
                            <td key={action.key} style={{ textAlign: 'center', padding: '0.6rem 0.75rem' }}>
                              <div
                                onClick={() => handleTogglePermission(user.id, mod.key, action.key)}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                  width: 28, height: 28, borderRadius: 7, cursor: 'pointer',
                                  border: isCustom
                                    ? `2px solid ${effective ? '#FF6B35' : '#FF6B3560'}`
                                    : `2px solid ${effective ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.08)'}`,
                                  background: isCustom
                                    ? (effective ? 'rgba(255,107,53,0.25)' : 'rgba(255,107,53,0.05)')
                                    : (effective ? 'rgba(255,255,255,0.1)' : 'transparent'),
                                  transition: 'all 0.15s ease',
                                  userSelect: 'none',
                                }}
                                title={`${action.label} — ${isCustom ? 'Exception personnalisée' : 'Hérité du rôle'}`}
                              >
                                {effective
                                  ? <span style={{ fontSize: 14, color: isCustom ? '#FF6B35' : '#94A3B8' }}>✓</span>
                                  : <span style={{ fontSize: 12, color: isCustom ? '#FF6B3560' : '#334155'  }}>✗</span>
                                }
                              </div>
                            </td>
                          )
                        })}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Boutons d'action */}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <button
                onClick={() => setExpandedUserId(null)}
                style={{
                  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                  color: '#94A3B8', borderRadius: 10, padding: '0.6rem 1.25rem',
                  cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                }}
              >
                Annuler
              </button>
              <button
                onClick={() => handleSauvegarder(user.id)}
                disabled={savingPerm || !hasChanges}
                style={{
                  background: hasChanges && !savingPerm
                    ? 'linear-gradient(135deg, #FF6B35, #E55A2B)'
                    : 'rgba(255,107,53,0.2)',
                  border: 'none', borderRadius: 10, padding: '0.6rem 1.5rem',
                  color: hasChanges && !savingPerm ? 'white' : '#64748B',
                  cursor: hasChanges && !savingPerm ? 'pointer' : 'not-allowed',
                  fontSize: '0.85rem', fontWeight: 700,
                  boxShadow: hasChanges && !savingPerm ? '0 4px 14px rgba(255,107,53,0.3)' : 'none',
                }}
              >
                {savingPerm ? '⏳ Enregistrement…' : '💾 Enregistrer les accès'}
              </button>
            </div>
          </div>
        </td>
      </tr>
    )
  }

  return (
    <div style={{ padding: '2rem', fontFamily: "'Bricolage Grotesque', sans-serif", color: 'white' }}>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 80, right: 20, zIndex: 99999,
          background: toast.type === 'success' ? 'rgba(16,185,129,0.95)' : 'rgba(239,68,68,0.95)',
          color: 'white', borderRadius: 12, padding: '0.75rem 1.25rem',
          fontWeight: 600, boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }}>
          {toast.type === 'success' ? '✅' : '⚠️'} {toast.text}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800 }}>👥 Utilisateurs</h2>
          <p style={{ margin: '0.25rem 0 0', color: '#64748B', fontSize: '0.85rem' }}>
            {users.filter(u => u.actif).length} actif(s) · {users.length} total
          </p>
        </div>
        <button
          onClick={() => setShowInvite(v => !v)}
          style={{
            background: 'linear-gradient(135deg,#FF6B35,#E55A2B)',
            border: 'none', borderRadius: 12, padding: '0.7rem 1.25rem',
            color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: '0.875rem',
          }}>
          {showInvite ? '✕ Annuler' : '+ Inviter'}
        </button>
      </div>

      {/* Formulaire invitation */}
      {showInvite && (
        <form onSubmit={handleInviter} style={{
          background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 16, padding: '1.5rem', marginBottom: '1.5rem',
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '1rem', alignItems: 'end',
        }}>
          <div>
            <label style={{ display: 'block', color: '#94A3B8', fontSize: '0.75rem', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Nom</label>
            <input value={invite.nom} required placeholder="Jean Dupont"
              onChange={e => setInvite(p => ({ ...p, nom: e.target.value }))} style={inp()} />
          </div>
          <div>
            <label style={{ display: 'block', color: '#94A3B8', fontSize: '0.75rem', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email</label>
            <input type="email" value={invite.email} required placeholder="jean@entreprise.cm"
              onChange={e => setInvite(p => ({ ...p, email: e.target.value }))} style={inp()} />
          </div>
          <div>
            <label style={{ display: 'block', color: '#94A3B8', fontSize: '0.75rem', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Rôle</label>
            <select value={invite.role} onChange={e => setInvite(p => ({ ...p, role: e.target.value }))}
              style={inp({ background: '#1E293B' })}>
              {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="submit" disabled={invLoading} style={{
              padding: '0.8rem 1rem', background: '#10B981', border: 'none',
              borderRadius: 10, color: 'white', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
            }}>
              {invLoading ? '…' : '✉️ Inviter'}
            </button>
          </div>
        </form>
      )}

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#64748B' }}>Chargement…</div>
      ) : (
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.05)' }}>
                {['Utilisateur', 'Email', 'Rôle', 'Statut', 'Accès', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '0.9rem 1rem', textAlign: 'left', color: '#64748B', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const rc = ROLE_COLORS[u.role] || ROLE_COLORS.lecture
                const isMe = u.id === moi?.id
                const isExpanded = expandedUserId === u.id
                const personnalise = aDesAccesPersonnalises(u)

                return (
                  <React.Fragment key={u.id}>
                    <tr style={{
                      borderTop: '1px solid rgba(255,255,255,0.05)',
                      background: isExpanded ? 'rgba(255,107,53,0.04)' : 'transparent',
                    }}>
                      <td style={{ padding: '0.9rem 1rem', fontWeight: 600 }}>
                        {u.nom} {u.prenom || ''}
                        {isMe && <span style={{ color: '#00D4AA', fontSize: '0.7rem', marginLeft: 6 }}>(moi)</span>}
                      </td>
                      <td style={{ padding: '0.9rem 1rem', color: '#94A3B8', fontSize: '0.875rem' }}>{u.email}</td>
                      <td style={{ padding: '0.9rem 1rem' }}>
                        {isMe ? (
                          <span style={{ background: rc.bg, border: `1px solid ${rc.border}`, color: rc.text, padding: '0.25rem 0.75rem', borderRadius: 20, fontSize: '0.78rem', fontWeight: 700 }}>
                            {ROLES.find(r => r.value === u.role)?.label || u.role}
                          </span>
                        ) : (
                          <select value={u.role} onChange={e => handleRole(u.id, e.target.value)}
                            style={{ background: rc.bg, border: `1px solid ${rc.border}`, color: rc.text, padding: '0.25rem 0.75rem', borderRadius: 20, fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', outline: 'none' }}>
                            {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                          </select>
                        )}
                      </td>
                      <td style={{ padding: '0.9rem 1rem' }}>
                        <span style={{
                          background: u.actif ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
                          color: u.actif ? '#10B981' : '#EF4444',
                          padding: '0.2rem 0.65rem', borderRadius: 20, fontSize: '0.78rem', fontWeight: 600,
                        }}>
                          {u.actif ? '● Actif' : '● Inactif'}
                        </span>
                      </td>
                      <td style={{ padding: '0.9rem 1rem' }}>
                        {!isMe ? (
                          <button
                            onClick={() => handleToggleAcces(u.id)}
                            style={{
                              background: isExpanded
                                ? 'rgba(255,107,53,0.2)'
                                : personnalise ? 'rgba(255,107,53,0.12)' : 'rgba(0,212,170,0.1)',
                              border: `1px solid ${isExpanded
                                ? 'rgba(255,107,53,0.5)'
                                : personnalise ? 'rgba(255,107,53,0.35)' : 'rgba(0,212,170,0.35)'}`,
                              color: isExpanded ? '#FF6B35' : personnalise ? '#FF6B35' : '#00D4AA',
                              padding: '0.25rem 0.75rem', borderRadius: 20,
                              fontSize: '0.72rem', fontWeight: 700,
                              cursor: 'pointer', whiteSpace: 'nowrap',
                            }}
                          >
                            {isExpanded ? '▲ ' : '▼ '}
                            {personnalise ? '✦ Personnalisé' : 'Par rôle'}
                          </button>
                        ) : (
                          <span style={{
                            color: '#334155', fontSize: '0.72rem', fontWeight: 600,
                            padding: '0.25rem 0.75rem',
                          }}>
                            —
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '0.9rem 1rem' }}>
                        {!isMe && u.actif && (
                          <button onClick={() => handleDesactiver(u.id)} style={{
                            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                            color: '#FCA5A5', borderRadius: 8, padding: '0.35rem 0.75rem',
                            cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
                          }}>
                            Désactiver
                          </button>
                        )}
                      </td>
                    </tr>
                    {isExpanded && renderPanneauAcces(u)}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Légende rôles */}
      <div style={{ marginTop: '1.5rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px,1fr))', gap: '0.6rem' }}>
        {ROLES.map(r => {
          const rc = ROLE_COLORS[r.value]
          return (
            <div key={r.value} style={{ background: rc.bg, border: `1px solid ${rc.border}30`, borderRadius: 10, padding: '0.7rem 0.9rem' }}>
              <div style={{ color: rc.text, fontWeight: 700, fontSize: '0.85rem' }}>{r.label}</div>
              <div style={{ color: '#64748B', fontSize: '0.75rem', marginTop: '0.2rem' }}>{r.desc}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
